import {Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {loggerFactory} from "@cm/browser-log"
import potpack, {PotpackBox} from "potpack"
import {DEFAULT_FLOAT_TEXTURE_TYPE, getAuthoredMaterialSide} from "@template-editor/helpers/three-utils"
import {Subject} from "rxjs"
import {getJitterVector, getReceiverJitterVector} from "@app/template-editor/helpers/jitter"
import {
    ensureUVSideness,
    getGeometrySurfaceArea,
    getMinMaxUVs,
    getUVSurfaceArea,
    scaleUVs,
    UVBounds,
    uvOverlappingRatio,
} from "@template-editor/helpers/uv-shadow-map/geometry"
import {
    getOriginalLightRenderData,
    getOriginalMeshRenderData,
    getOriginalObjectRenderData,
    OriginalLightRenderData,
    OriginalMeshRenderData,
    OriginalObjectRenderData,
    restoreOriginalLightRenderData,
    restoreOriginalMeshRenderData,
    restoreOriginalObjectRenderData,
    ThreeLightWithShadow,
} from "@template-editor/helpers/uv-shadow-map/render-state"
import {
    createShadowLightProxy,
    getSceneBoundingSphere,
    registerShadowLightProxies,
    ShadowLightProxy,
} from "@template-editor/helpers/uv-shadow-map/light-proxies"
import {getDummyMaterial, getMaskMaterial, getShadowMaterial} from "@template-editor/helpers/uv-shadow-map/materials"

export class ThreeProgressiveUVShadowMap {
    private uvShadowMapTemp: THREE.RenderTarget
    private uvShadowMap: THREE.RenderTarget
    private shadowMapMask: THREE.RenderTarget
    private shadowMapMaskDirty = false
    private currentMask: THREE.RenderTarget | undefined
    private currentMaskTemp: THREE.RenderTarget | undefined
    private previousMap = TSL.texture()
    private previousMask = TSL.texture()
    private blendMode = TSL.uniform(-1)
    private iteration = TSL.uniform(-1)
    private uvNode = TSL.uv(3)
    private dummyRenderTarget: THREE.RenderTarget = new THREE.RenderTarget(1, 1)
    private shadowMaterials: {[key in THREE.Side]: THREE.Material}
    private dummyMaterials: {[key in THREE.Side]: THREE.Material}
    private maskMaterials: {[key in THREE.Side]: THREE.Material}
    private meshes: Set<THREE.Mesh<THREE.BufferGeometry, THREE.Material>> = new Set()
    private shadowProxies = new Map<THREE.Light, THREE.Light & ShadowLightProxy>()
    private previousShadowLightKey = ""
    private debugMaterial: THREE.MeshBasicMaterial
    private camera = new THREE.PerspectiveCamera()
    private debugObject: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> | undefined = undefined
    private resolutionX = TSL.uniform(-1)
    private resolutionY = TSL.uniform(-1)
    private receiverJitter = TSL.uniform(new THREE.Vector2())
    private blurPlane: THREE.Mesh<THREE.PlaneGeometry, THREE.Material>

    private detachedUVShadowMap: Subject<THREE.Mesh<THREE.BufferGeometry, THREE.Material>> = new Subject<THREE.Mesh<THREE.BufferGeometry, THREE.Material>>()
    detachedUVShadowMap$ = this.detachedUVShadowMap.asObservable()

    private attachedUVShadowMap: Subject<THREE.Mesh<THREE.BufferGeometry, THREE.Material>> = new Subject<THREE.Mesh<THREE.BufferGeometry, THREE.Material>>()
    attachedUVShadowMap$ = this.attachedUVShadowMap.asObservable()

    constructor(
        private renderer: THREE.WebGPURenderer,
        private resolution: number,
    ) {
        registerShadowLightProxies(this.renderer)

        this.uvShadowMapTemp = new THREE.RenderTarget(1, 1, {
            type: DEFAULT_FLOAT_TEXTURE_TYPE,
            generateMipmaps: false,
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RedFormat,
            colorSpace: THREE.NoColorSpace,
        })
        this.uvShadowMap = new THREE.RenderTarget(1, 1, {
            type: DEFAULT_FLOAT_TEXTURE_TYPE,
            generateMipmaps: false,
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RedFormat,
            colorSpace: THREE.NoColorSpace,
        })
        this.uvShadowMap.texture.channel = 3
        this.uvShadowMap.texture.flipY = false

        this.shadowMapMask = new THREE.RenderTarget(1, 1, {
            generateMipmaps: false,
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RedFormat,
            colorSpace: THREE.NoColorSpace,
        })

        this.debugMaterial = new THREE.MeshBasicMaterial({map: this.uvShadowMap.texture})

        const receiverJitterUV = this.receiverJitter.div(TSL.vec2(this.resolutionX, this.resolutionY))

        this.shadowMaterials = {
            [THREE.FrontSide]: getShadowMaterial(THREE.FrontSide, this.uvNode, this.previousMap, this.iteration, receiverJitterUV),
            [THREE.BackSide]: getShadowMaterial(THREE.BackSide, this.uvNode, this.previousMap, this.iteration, receiverJitterUV),
            [THREE.DoubleSide]: getShadowMaterial(THREE.DoubleSide, this.uvNode, this.previousMap, this.iteration, receiverJitterUV),
        }

        this.dummyMaterials = {
            [THREE.FrontSide]: getDummyMaterial(THREE.FrontSide),
            [THREE.BackSide]: getDummyMaterial(THREE.BackSide),
            [THREE.DoubleSide]: getDummyMaterial(THREE.DoubleSide),
        }

        this.maskMaterials = {
            [THREE.FrontSide]: getMaskMaterial(THREE.FrontSide, this.uvNode),
            [THREE.BackSide]: getMaskMaterial(THREE.BackSide, this.uvNode),
            [THREE.DoubleSide]: getMaskMaterial(THREE.DoubleSide, this.uvNode),
        }

        const blurMaterial = new THREE.NodeMaterial()
        blurMaterial.vertexNode = TSL.vec4(TSL.sub(TSL.uv(), TSL.vec2(0.5)).mul(2), 1, 1)

        const uvNode = TSL.uv().flipY()
        const pixelOffsetX = TSL.float(1.0).div(this.resolutionX)
        const pixelOffsetY = TSL.float(1.0).div(this.resolutionY)

        const resolve = TSL.Fn(() => {
            const result = TSL.vec4(1.0).toVar()

            TSL.If(this.blendMode.equal(0.0), () => {
                TSL.If(this.previousMask.sample(uvNode).r.greaterThan(0.0), () => {
                    result.assign(this.previousMap.sample(uvNode))
                }).Else(() => {
                    const sumColor = TSL.vec4(0.0).toVar()
                    const sumMask = TSL.float(0.0).toVar()

                    TSL.Loop({type: "int", start: -1, end: 2}, {type: "int", start: -1, end: 2}, ({i, j}) => {
                        const offsetUV = uvNode.add(TSL.vec2(pixelOffsetX.mul(i), pixelOffsetY.mul(j)))
                        const mask = this.previousMask.sample(offsetUV).r
                        TSL.If(mask.greaterThan(0.0), () => {
                            sumColor.addAssign(this.previousMap.sample(offsetUV))
                            sumMask.addAssign(1)
                        })
                    })

                    TSL.If(sumMask.greaterThan(0.0), () => {
                        result.assign(sumColor.div(sumMask))
                    })
                })
            })
                .ElseIf(this.blendMode.equal(1.0), () => {
                    const sumColor = TSL.vec4(0.0).toVar()
                    const sumMask = TSL.float(0.0).toVar()

                    TSL.Loop({type: "int", start: -1, end: 2}, {type: "int", start: -1, end: 2}, ({i, j}) => {
                        const offsetUV = uvNode.add(TSL.vec2(pixelOffsetX.mul(i), pixelOffsetY.mul(j)))
                        const mask = this.previousMask.sample(offsetUV)
                        sumMask.addAssign(mask)
                        sumColor.addAssign(this.previousMap.sample(offsetUV).mul(mask))
                    })

                    TSL.If(sumMask.greaterThan(0.0), () => {
                        result.assign(sumColor.div(sumMask))
                    })
                })
                .Else(() => {
                    TSL.Loop({type: "int", start: -1, end: 2}, {type: "int", start: -1, end: 2}, ({i, j}) => {
                        const offsetUV = uvNode.add(TSL.vec2(pixelOffsetX.mul(i), pixelOffsetY.mul(j)))
                        const mask = this.previousMask.sample(offsetUV)
                        result.assign(result.max(mask))
                    })
                })

            return result
        })

        blurMaterial.fragmentNode = resolve()
        blurMaterial.depthWrite = false

        this.blurPlane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), blurMaterial)
        this.blurPlane.frustumCulled = false
        this.blurPlane.renderOrder = 0
        this.blurPlane.material.depthWrite = false

        this.reset()
    }

    getResolution() {
        return this.resolution
    }

    setResolution(resolution: number) {
        this.resolution = resolution
    }

    has(mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>) {
        return this.meshes.has(mesh)
    }

    onDisposingMesh(mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>) {
        this.meshes.delete(mesh)
    }

    dispose() {
        for (const oldMesh of this.meshes) {
            oldMesh.geometry.deleteAttribute("uv3")
            this.detachedUVShadowMap.next(oldMesh)
        }
        for (const proxy of this.shadowProxies.values()) proxy.dispose()
        for (const dummyMaterial of Object.values(this.dummyMaterials)) dummyMaterial.dispose()
        for (const shadowMaterial of Object.values(this.shadowMaterials)) shadowMaterial.dispose()
        for (const maskMaterial of Object.values(this.maskMaterials)) maskMaterial.dispose()
        this.debugMaterial.dispose()
        this.dummyRenderTarget.dispose()
        this.shadowMapMask.dispose()
        if (this.currentMask) {
            this.currentMask.dispose()
            this.currentMask = undefined
        }
        if (this.currentMaskTemp) {
            this.currentMaskTemp.dispose()
            this.currentMaskTemp = undefined
        }
        this.uvShadowMap.dispose()
        this.uvShadowMapTemp.dispose()
        this.blurPlane.geometry.dispose()
        this.blurPlane.material.dispose()
        this.meshes.clear()
        if (this.debugObject) {
            this.debugObject.geometry.dispose()
            this.debugObject.material.dispose()
            this.debugObject = undefined
        }
    }

    getUVShadowMap() {
        return this.uvShadowMap.texture
    }

    attachToMeshes(newMeshes: Set<THREE.Mesh<THREE.BufferGeometry, THREE.Material>>) {
        for (const oldMesh of this.meshes) {
            oldMesh.geometry.deleteAttribute("uv3")
            if (!newMeshes.has(oldMesh)) this.detachedUVShadowMap.next(oldMesh)
        }

        const uvBoxes: (PotpackBox & {
            mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>
            uvBounds: UVBounds
        })[] = []

        for (const newMesh of newMeshes) {
            const existingUvs = newMesh.geometry.getAttribute("uv")
            if (!existingUvs || existingUvs.count === 0) {
                ThreeProgressiveUVShadowMap.logger.warn("No UVs found on mesh, skipping", newMesh)
                continue
            }

            const existingUv3s = newMesh.geometry.getAttribute("uv3")
            if (existingUv3s) {
                ThreeProgressiveUVShadowMap.logger.warn("UV with index 3 already exist on mesh, skipping", newMesh)
                continue
            }

            const surfaceArea = getGeometrySurfaceArea(newMesh.geometry.getAttribute("position"), newMesh.geometry.getIndex())
            if (surfaceArea <= 0 || !isFinite(surfaceArea)) {
                ThreeProgressiveUVShadowMap.logger.warn("Mesh surface is invalid, skipping", newMesh)
                continue
            }

            const uv3 = existingUvs.clone()

            const overlappingRatio = uvOverlappingRatio(uv3, newMesh.geometry.getIndex(), 64)
            if (overlappingRatio > 0.1) {
                ThreeProgressiveUVShadowMap.logger.warn(`Mesh uv surface is overlapping (ratio: ${overlappingRatio}), skipping`, newMesh)
                continue
            }

            const side = getAuthoredMaterialSide(newMesh.material)
            if (side !== THREE.DoubleSide) ensureUVSideness(uv3, newMesh.geometry.getIndex(), side)

            const uvArea = getUVSurfaceArea(uv3, newMesh.geometry.getIndex())

            if (uvArea <= 0 || !isFinite(uvArea)) {
                ThreeProgressiveUVShadowMap.logger.warn("Mesh uv surface is invalid, skipping", newMesh)
                continue
            }

            const surfaceScale = Math.sqrt(surfaceArea)
            const uvScale = Math.sqrt(uvArea)
            const scale = surfaceScale / uvScale

            if (scale <= 0 || !isFinite(scale)) {
                ThreeProgressiveUVShadowMap.logger.warn("Mesh scaling is invalid, skipping", newMesh)
                continue
            }

            newMesh.geometry.setAttribute("uv3", uv3)
            scaleUVs(uv3, scale)
            const uvBounds = getMinMaxUVs(uv3)

            uvBoxes.push({
                w: uvBounds.maxU - uvBounds.minU,
                h: uvBounds.maxV - uvBounds.minV,
                mesh: newMesh,
                uvBounds,
            })

            if (!this.meshes.has(newMesh)) this.attachedUVShadowMap.next(newMesh)
        }
        this.meshes = new Set(uvBoxes.map((box) => box.mesh))

        const totalBoxArea = uvBoxes.reduce((acc, box) => acc + box.w * box.h, 0)
        const padding = Math.sqrt(totalBoxArea) * 0.01 //1% padding
        for (const uvBox of uvBoxes) {
            uvBox.w += padding * 2
            uvBox.h += padding * 2
        }

        const dimensions = potpack(uvBoxes)

        uvBoxes.forEach(({mesh, x, y, uvBounds}) => {
            const uv3 = mesh.geometry.getAttribute("uv3")
            if (x === undefined || y === undefined) throw new Error("x or y is undefined")

            const {minU, minV} = uvBounds

            for (let i = 0; i < uv3.array.length; i += uv3.itemSize) {
                uv3.array[i] = (uv3.array[i] - minU + x + padding) / dimensions.w
                uv3.array[i + 1] = (uv3.array[i + 1] - minV + y + padding) / dimensions.h
                if (uv3.array[i] >= 0 && uv3.array[i] <= 1 && uv3.array[i + 1] >= 0 && uv3.array[i + 1] <= 1) {
                    //nothing to do
                } else {
                    ThreeProgressiveUVShadowMap.logger.debug("uv3 out of bounds", uv3.array[i], uv3.array[i + 1])
                }
            }

            uv3.needsUpdate = true
        })

        const resolutionSq = this.resolution * this.resolution
        const dimensionSq = dimensions.w * dimensions.h
        const scale = Math.sqrt(resolutionSq / dimensionSq)

        this.resolutionX.value = Math.max(1, Math.round(dimensions.w * scale))
        this.resolutionY.value = Math.max(1, Math.round(dimensions.h * scale))

        this.uvShadowMapTemp.setSize(this.resolutionX.value, this.resolutionY.value)
        this.uvShadowMap.setSize(this.resolutionX.value, this.resolutionY.value)
        this.shadowMapMask.setSize(this.resolutionX.value, this.resolutionY.value)
        if (this.currentMask) this.currentMask.setSize(this.resolutionX.value, this.resolutionY.value)
        if (this.currentMaskTemp) this.currentMaskTemp.setSize(this.resolutionX.value, this.resolutionY.value)
        this.shadowMapMaskDirty = true

        this.reset()

        if (this.debugObject) this.debugObject.scale.set(1, this.resolutionY.value / this.resolutionX.value, 1)
    }

    compute(scene: THREE.Scene, currentIteration: number, iterations = 1) {
        if (this.meshes.size === 0) return
        if (this.resolutionX.value <= 0 || this.resolutionY.value <= 0) return

        const tempScene = new THREE.Scene()

        const lightObjects: (THREE.Light | THREE.Mesh)[] = []
        scene.traverseVisible((object) => {
            if (object instanceof THREE.Light) {
                if (object.castShadow) lightObjects.push(object)
            } else if (object instanceof THREE.Mesh && (object.castShadow || object.receiveShadow)) {
                lightObjects.push(object)
            }
        })

        // When the set of shadow lights changes at runtime, three rebuilds its internal lights/shadow nodes, and a
        // reused light proxy carries over state that's now inconsistent with the rebuilt nodes -> its shadow stops
        // re-rendering (renders once, then no jitter / no response to moves). A fresh load works because everything
        // is built clean. So on any light-set change we drop the proxies and rebuild them fresh, matching that path.
        if (currentIteration === 0) {
            const shadowLightKey = lightObjects
                .filter((o): o is THREE.Light => o instanceof THREE.Light)
                .map((light) => light.uuid)
                .sort()
                .join(",")
            if (shadowLightKey !== this.previousShadowLightKey) {
                for (const proxy of this.shadowProxies.values()) proxy.dispose()
                this.shadowProxies.clear()
                this.previousShadowLightKey = shadowLightKey
            }
        }

        const staleProxies = new Set(this.shadowProxies.keys())

        const originalObjectData = new Map<THREE.Object3D, OriginalObjectRenderData>()
        lightObjects.forEach((object) => {
            if (object instanceof THREE.Light) {
                let proxy = this.shadowProxies.get(object)
                if (!proxy) {
                    proxy = createShadowLightProxy(object) ?? undefined
                    if (proxy) this.shadowProxies.set(object, proxy)
                }
                if (proxy) {
                    staleProxies.delete(object)
                    proxy.sync()
                    proxy.addToScene(tempScene)
                    return
                }
            }

            originalObjectData.set(object, getOriginalObjectRenderData(object))
            tempScene.attach(object)
        })

        for (const light of staleProxies) {
            const lightProxy = this.shadowProxies.get(light)
            if (!lightProxy) throw new Error("Reference light not found")
            lightProxy.dispose()
            this.shadowProxies.delete(light)
        }

        if (currentIteration === 0 && this.shadowProxies.size > 0) {
            const sceneBoundingSphere = getSceneBoundingSphere(lightObjects)
            for (const proxy of this.shadowProxies.values()) proxy.configureForScene?.(sceneBoundingSphere)
        }

        const rendererState = THREE.RendererUtils.saveRendererState(this.renderer)
        const oldShadowMapEnabled = this.renderer.shadowMap.enabled

        const originalMeshData = new Map<THREE.Mesh, OriginalMeshRenderData>()
        const originalLightData = new Map<THREE.Light, OriginalLightRenderData>()
        lightObjects.forEach((object) => {
            if (object instanceof THREE.Mesh) {
                const mesh = object as THREE.Mesh
                originalMeshData.set(mesh, getOriginalMeshRenderData(mesh))
            } else if (object instanceof THREE.Light) {
                const light = object as THREE.Light
                originalLightData.set(light, getOriginalLightRenderData(light))
            }
        })

        this.renderer.shadowMap.enabled = true
        this.renderer.setClearColor(0xffffff, 1)

        for (let i = 0; i < iterations; i++) {
            this.iteration.value = currentIteration + i

            //1. Pass updates the internal Three.JS shadow maps using all meshes
            lightObjects.forEach((object) => {
                if (object instanceof THREE.Mesh) {
                    const mesh = object as THREE.Mesh

                    const originalData = originalMeshData.get(mesh)
                    if (!originalData) throw new Error("Original mesh data not found")

                    restoreOriginalMeshRenderData(mesh, originalData)

                    if (Array.isArray(mesh.material)) mesh.material = mesh.material.map((material) => this.dummyMaterials[getAuthoredMaterialSide(material)])
                    else mesh.material = this.dummyMaterials[getAuthoredMaterialSide(mesh.material)]

                    mesh.frustumCulled = false
                }
            })

            tempScene.traverseVisible((object) => {
                if (object instanceof THREE.Light) {
                    const light = object as ThreeLightWithShadow
                    if (light.shadow) {
                        light.shadow.autoUpdate = false
                        light.shadow.needsUpdate = true
                    }
                }
            })

            const [offsetX, offsetY] = getJitterVector(this.iteration.value)

            for (const proxy of this.shadowProxies.values()) proxy.jitter(offsetX, offsetY)

            const [receiverOffsetX, receiverOffsetY] = getReceiverJitterVector(this.iteration.value)
            this.receiverJitter.value.set(receiverOffsetX, receiverOffsetY)

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const {_nodes} = this.renderer as any
            if (!_nodes) throw new Error("Renderer nodes not found")
            const nodeFrame = _nodes.nodeFrame
            nodeFrame.update()
            this.renderer.setRenderTarget(this.dummyRenderTarget)
            this.renderer.clear()
            this.renderer.render(tempScene, this.camera)

            tempScene.traverseVisible((object) => {
                if (object instanceof THREE.Light) {
                    const light = object as ThreeLightWithShadow
                    if (light.shadow) {
                        light.shadow.autoUpdate = false
                        light.shadow.needsUpdate = false
                    }
                }
            })

            //2. Pass updates the uv shadow map with only the meshes that receive shadows
            lightObjects.forEach((object) => {
                if (object instanceof THREE.Mesh) {
                    if (object.receiveShadow && this.meshes.has(object as THREE.Mesh<THREE.BufferGeometry, THREE.Material>)) {
                        const mesh = object as THREE.Mesh<THREE.BufferGeometry, THREE.Material>

                        mesh.material = this.shadowMaterials[getAuthoredMaterialSide(mesh.material)]
                    } else object.visible = false
                }
            })

            const activeMap = this.iteration.value % 2 === 0 ? this.uvShadowMap : this.uvShadowMapTemp
            const inactiveMap = this.iteration.value % 2 === 0 ? this.uvShadowMapTemp : this.uvShadowMap

            this.previousMap.value = inactiveMap.texture

            // Keep the color buffer: under receiver jitter, texels at chart borders flicker in and out of coverage,
            // and a cleared iteration would replace their accumulated history with the clear color
            const previousAutoClear = this.renderer.autoClear
            this.renderer.autoClear = false
            this.renderer.setRenderTarget(activeMap)
            this.renderer.clear(false, true, true)
            this.renderer.render(tempScene, this.camera)
            this.renderer.autoClear = previousAutoClear
        }

        if (this.shadowMapMaskDirty) {
            this.renderer.setClearColor(0x000000, 1)
            this.renderer.setRenderTarget(this.shadowMapMask)

            lightObjects.forEach((object) => {
                if (object instanceof THREE.Mesh) {
                    if (object.receiveShadow && this.meshes.has(object as THREE.Mesh<THREE.BufferGeometry, THREE.Material>)) {
                        const mesh = object as THREE.Mesh<THREE.BufferGeometry, THREE.Material>
                        mesh.material = this.maskMaterials[getAuthoredMaterialSide(mesh.material)]
                    }
                }
            })

            this.renderer.clear()
            this.renderer.render(tempScene, this.camera)
            this.shadowMapMaskDirty = false
        }

        originalMeshData.forEach((originalData, mesh) => restoreOriginalMeshRenderData(mesh, originalData))
        originalLightData.forEach((originalData, light) => restoreOriginalLightRenderData(light, originalData))
        originalObjectData.forEach((originalData, object) => restoreOriginalObjectRenderData(object, originalData))

        const activeMap = this.iteration.value % 2 === 0 ? this.uvShadowMap : this.uvShadowMapTemp
        const inactiveMap = this.iteration.value % 2 === 0 ? this.uvShadowMapTemp : this.uvShadowMap

        this.renderer.copyTextureToTexture(activeMap.texture, inactiveMap.texture)

        tempScene.clear()
        tempScene.add(this.blurPlane)

        this.blendMode.value = 0
        this.previousMask.value = this.shadowMapMask.texture
        this.previousMap.value = this.uvShadowMapTemp.texture

        this.renderer.setRenderTarget(this.uvShadowMap)
        this.renderer.clear()
        this.renderer.render(tempScene, this.camera)

        THREE.RendererUtils.restoreRendererState(this.renderer, rendererState)
        this.renderer.shadowMap.enabled = oldShadowMapEnabled
    }

    smoothResult(currentIteration: number, iterations = 1) {
        if (this.meshes.size === 0) return
        if (this.resolutionX.value <= 0 || this.resolutionY.value <= 0) return

        if (this.shadowMapMaskDirty) return

        if (!this.currentMask) {
            this.currentMask = new THREE.RenderTarget(this.resolutionX.value, this.resolutionY.value, {
                generateMipmaps: false,
                minFilter: THREE.NearestFilter,
                magFilter: THREE.NearestFilter,
                format: THREE.RedFormat,
                colorSpace: THREE.NoColorSpace,
            })
        }

        if (!this.currentMaskTemp) {
            this.currentMaskTemp = new THREE.RenderTarget(this.resolutionX.value, this.resolutionY.value, {
                generateMipmaps: false,
                minFilter: THREE.NearestFilter,
                magFilter: THREE.NearestFilter,
                format: THREE.RedFormat,
                colorSpace: THREE.NoColorSpace,
            })
        }

        const rendererState = THREE.RendererUtils.saveRendererState(this.renderer)
        this.renderer.setClearColor(0x000000, 1)

        const tempScene = new THREE.Scene()
        tempScene.add(this.blurPlane)

        for (let i = 0; i < iterations; i++) {
            this.iteration.value = currentIteration + i

            const activeMap = this.iteration.value % 2 === 0 ? this.uvShadowMap : this.uvShadowMapTemp
            const inactiveMap = this.iteration.value % 2 === 0 ? this.uvShadowMapTemp : this.uvShadowMap

            const activeCurrentMask = this.iteration.value % 2 === 0 ? this.currentMask : this.currentMaskTemp
            const inactiveCurrentMask =
                this.iteration.value === 0 ? this.shadowMapMask : this.iteration.value % 2 === 0 ? this.currentMaskTemp : this.currentMask

            this.blendMode.value = 1
            this.previousMap.value = inactiveMap.texture
            this.previousMask.value = inactiveCurrentMask.texture

            this.renderer.setRenderTarget(activeMap)
            this.renderer.clear()
            this.renderer.render(tempScene, this.camera)

            this.blendMode.value = 2

            this.renderer.setRenderTarget(activeCurrentMask)
            this.renderer.clear()
            this.renderer.render(tempScene, this.camera)
        }

        const activeMap = this.iteration.value % 2 === 0 ? this.uvShadowMap : this.uvShadowMapTemp
        const inactiveMap = this.iteration.value % 2 === 0 ? this.uvShadowMapTemp : this.uvShadowMap

        this.renderer.copyTextureToTexture(activeMap.texture, inactiveMap.texture)

        THREE.RendererUtils.restoreRendererState(this.renderer, rendererState)
    }

    getDebugObject() {
        if (!this.debugObject) {
            const labelMaterial = new THREE.MeshBasicMaterial({map: this.uvShadowMapTemp.texture, side: THREE.DoubleSide})
            const labelPlane = new THREE.PlaneGeometry(100, 100)
            this.debugObject = new THREE.Mesh(labelPlane, labelMaterial)
            this.debugObject.position.y = 250
            if (this.resolutionX.value > 0 && this.resolutionY.value > 0) this.debugObject.scale.set(1, this.resolutionY.value / this.resolutionX.value, 1)
            return this.debugObject
        } else return this.debugObject
    }

    reset() {
        this.fillRenderTargetWithColor(this.uvShadowMapTemp, new THREE.Color(0xffffff))
        this.fillRenderTargetWithColor(this.uvShadowMap, new THREE.Color(0xffffff))
        this.fillRenderTargetWithColor(this.shadowMapMask, new THREE.Color(0xffffff))
        this.shadowMapMaskDirty = true
        if (this.currentMask) this.fillRenderTargetWithColor(this.currentMask, new THREE.Color(0xffffff))
        if (this.currentMaskTemp) this.fillRenderTargetWithColor(this.currentMaskTemp, new THREE.Color(0xffffff))
    }

    private fillRenderTargetWithColor(renderTarget: THREE.RenderTarget, color: THREE.Color) {
        const rendererState = THREE.RendererUtils.saveRendererState(this.renderer)

        this.renderer.setRenderTarget(renderTarget)
        this.renderer.setClearColor(color, 1)
        this.renderer.clear()

        THREE.RendererUtils.restoreRendererState(this.renderer, rendererState)
    }

    private static readonly logger = loggerFactory.getLogger("scene")
}
